import { SnapshotModel } from '..';
import { Collections } from '../../constants';
import { BadRequestError } from '../../errors/ColumnErrors';
import {
  ResponseOrColumnError,
  wrapError,
  wrapSuccess
} from '../../types/responses';
import {
  BadRequestSyncAttempt,
  BaseSyncAttempt,
  ExternalErrorSyncAttempt,
  FinalizedSyncAttempt,
  InternalErrorSyncAttempt,
  SkippedSyncAttempt,
  SuccessSyncAttempt,
  SyncAttempt,
  SyncAttemptStatus
} from '../../types/syncAttempt';
import { getErrorReporter } from '../../utils/errors';
import { safeUpdateModel } from '../safeHandlers';
import { SerializedModel } from '../types';

export type SerializedSyncAttemptModel = SerializedModel<
  typeof Collections.syncAttempts
>;

const SYNC_ATTEMPT_FINALIZED = 'sync_attempt_finalized';
export type SyncAttemptFinalized = typeof SYNC_ATTEMPT_FINALIZED;

type FinalizationInputs<T extends FinalizedSyncAttempt> = Omit<
  T,
  keyof BaseSyncAttempt | 'finalizedAt'
>;

export class SyncAttemptModel extends SnapshotModel<
  SyncAttempt,
  typeof Collections.syncAttempts
> {
  get type() {
    return Collections.syncAttempts;
  }

  isPending() {
    return this.modelData.status === SyncAttemptStatus.IN_PROGRESS;
  }

  isFinalized(): this is SnapshotModel<
    FinalizedSyncAttempt,
    typeof Collections.syncAttempts
  > {
    return (
      this.modelData.status !== SyncAttemptStatus.IN_PROGRESS &&
      !!this.modelData.finalizedAt
    );
  }

  isSuccess(): this is SnapshotModel<
    SuccessSyncAttempt,
    typeof Collections.syncAttempts
  > {
    return this.modelData.status === SyncAttemptStatus.SUCCESS;
  }

  isSkipped(): this is SnapshotModel<
    SkippedSyncAttempt,
    typeof Collections.syncAttempts
  > {
    return this.modelData.status === SyncAttemptStatus.SKIPPED;
  }

  isBadRequest(): this is SnapshotModel<
    BadRequestSyncAttempt,
    typeof Collections.syncAttempts
  > {
    return this.modelData.status === SyncAttemptStatus.BAD_REQUEST;
  }

  isColumnError(): this is SnapshotModel<
    InternalErrorSyncAttempt,
    typeof Collections.syncAttempts
  > {
    return this.modelData.status === SyncAttemptStatus.INTERNAL_ERROR;
  }

  isPublisherSystemError(): this is SnapshotModel<
    ExternalErrorSyncAttempt,
    typeof Collections.syncAttempts
  > {
    return this.modelData.status === SyncAttemptStatus.EXTERNAL_ERROR;
  }

  private alreadyFinalizedError() {
    return new BadRequestError(
      `Cannot finalize sync attempt with terminal status: ${this.modelData.status}`
    );
  }

  private async finalizeSyncAttempt(
    context: Partial<FinalizedSyncAttempt>
  ): Promise<ResponseOrColumnError<SyncAttemptFinalized>> {
    const finalizedAt = this.ctx.fieldValue().serverTimestamp();

    const [error] = await safeUpdateModel(this, {
      finalizedAt,
      ...context
    });

    if (error) {
      return wrapError(error);
    }

    /**
     * Logging sync attempt ID and status to allow for setting up monitors in
     * DataDog (particularly alerting devs proactively on 500 errors).
     */
    getErrorReporter().logInfo(
      `Finalized sync attempt ${this.id} with status ${context.status}`,
      {
        syncAttemptId: this.id,
        status: context.status
      }
    );

    return wrapSuccess(SYNC_ATTEMPT_FINALIZED);
  }

  async recordSuccess(
    context: FinalizationInputs<SuccessSyncAttempt>
  ): Promise<ResponseOrColumnError<SyncAttemptFinalized>> {
    if (!this.isPending()) {
      return wrapError(this.alreadyFinalizedError());
    }

    return this.finalizeSyncAttempt({
      status: SyncAttemptStatus.SUCCESS,
      ...context
    });
  }

  async recordSkipped(
    context: FinalizationInputs<SkippedSyncAttempt>
  ): Promise<ResponseOrColumnError<SyncAttemptFinalized>> {
    if (!this.isPending()) {
      return wrapError(this.alreadyFinalizedError());
    }

    return this.finalizeSyncAttempt({
      status: SyncAttemptStatus.SKIPPED,
      ...context
    });
  }

  async recordBadRequest(
    context: FinalizationInputs<BadRequestSyncAttempt>
  ): Promise<ResponseOrColumnError<SyncAttemptFinalized>> {
    if (!this.isPending()) {
      return wrapError(this.alreadyFinalizedError());
    }

    return this.finalizeSyncAttempt({
      status: SyncAttemptStatus.BAD_REQUEST,
      ...context
    });
  }

  async recordColumnError(
    context: FinalizationInputs<InternalErrorSyncAttempt>
  ): Promise<ResponseOrColumnError<SyncAttemptFinalized>> {
    if (!this.isPending()) {
      return wrapError(this.alreadyFinalizedError());
    }

    return this.finalizeSyncAttempt({
      status: SyncAttemptStatus.INTERNAL_ERROR,
      ...context
    });
  }

  async recordPublisherSystemError(
    context: FinalizationInputs<ExternalErrorSyncAttempt>
  ): Promise<ResponseOrColumnError<SyncAttemptFinalized>> {
    if (!this.isPending()) {
      return wrapError(this.alreadyFinalizedError());
    }

    return this.finalizeSyncAttempt({
      status: SyncAttemptStatus.EXTERNAL_ERROR,
      ...context
    });
  }
}
